/**
 * Durations as an editor shows and reads them: `m:ss` (or `h:mm:ss`), with
 * optional tenths. The domain stores milliseconds (`durationMsSchema`, at most
 * 24 h); the editor rounds to the tenth it shows.
 */
import { MAX_DURATION_MS } from '@acp/domain/primitives.js';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** `90_000` → `1:30`; `3_723_400` → `1:02:03.4`; null → `–`. */
export function formatDuration(ms: number | null): string {
  if (ms === null) return '–';
  const tenths = Math.round(ms / 100);
  const totalSeconds = Math.floor(tenths / 10);
  const fraction = tenths % 10;
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const base = h > 0 ? `${String(h)}:${pad2(m)}:${pad2(s)}` : `${String(m)}:${pad2(s)}`;
  return fraction === 0 ? base : `${base}.${String(fraction)}`;
}

/**
 * `1:30` → `90_000`; `45` → `45_000` (bare seconds); `1:02:03.5`; blank →
 * null (cleared). Anything else — or beyond 24 h — is `undefined`: not a
 * duration, and the form says so rather than writing it.
 */
export function parseDuration(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const match = /^(?:(\d+):)?(?:(\d+):)?(\d+(?:\.\d{1,3})?)$/.exec(trimmed);
  if (match === null) return undefined;
  const [, a, b, c] = match;
  const hours = a !== undefined && b !== undefined ? Number(a) : 0;
  const minutes = b !== undefined ? Number(b) : a !== undefined ? Number(a) : 0;
  const seconds = Number(c);
  // Only the leading unit may overflow: `90` seconds is fine, `1:90` is a typo.
  if (a !== undefined && seconds >= 60) return undefined;
  if (b !== undefined && minutes >= 60) return undefined;
  const ms = Math.round((hours * 3600 + minutes * 60 + seconds) * 1000);
  return ms <= MAX_DURATION_MS ? ms : undefined;
}
